// @ts-check
// Anterior e próximo na página do registro (doc 10, seção 1.4): a lista de onde o registro foi aberto (view, filtros,
// ordenação e, no kanban ou na tabela agrupada, a coluna) fica guardada ao abrir, e a página mostra "n de total" e anda
// por ela. Como no Twenty, a navegação é circular quando a lista inteira já é conhecida; quando ainda há páginas, o
// "próximo" do último carregado pede a página seguinte.

/**
 * @typedef {object} RecordListContext
 * @property {string} objectNameSingular
 * @property {string[]} recordIds na ordem da lista
 * @property {number | null} totalCount total que a lista diz ter (com filtros)
 * @property {boolean} hasNextPage ainda há registros depois do último id
 * @property {string | null} [endCursor] cursor para carregar a página seguinte
 * @property {any} [filter] filtro da lista, para carregar mais
 * @property {any[]} [orderBy] ordenação da lista
 * @property {string | null} [drilldown] recorte de um clique num gráfico, que segue ao carregar mais
 * @property {string | null} [viewName]
 * @property {string | null} [groupLabel] coluna do kanban ou seção da tabela agrupada
 * @property {string | null} [listPath] endereço da lista quando o registro foi aberto (volta do card passado)
 */

/**
 * @typedef {{ rank: number, total: number, previousId: string | null, nextId: string | null, needsMoreForNext: boolean }} RecordNavigation
 */

/**
 * Posição do registro na lista e os vizinhos; null quando o registro não está na lista.
 * @param {RecordListContext | null | undefined} context @param {string} objectNameSingular @param {string} recordId
 * @returns {RecordNavigation | null}
 */
export function computeRecordNavigation(context, objectNameSingular, recordId) {
  if (!context || context.objectNameSingular !== objectNameSingular) return null;
  const ids = context.recordIds;
  const index = ids.indexOf(recordId);
  if (index < 0) return null;
  const total = Math.max(context.totalCount ?? ids.length, ids.length);
  const isComplete = !context.hasNextPage && ids.length >= total;
  if (total <= 1) return { rank: 1, total: 1, previousId: null, nextId: null, needsMoreForNext: false };
  const isLastLoaded = index === ids.length - 1;
  return {
    rank: index + 1,
    total,
    previousId: index > 0 ? ids[index - 1] : isComplete ? ids[ids.length - 1] : null,
    nextId: !isLastLoaded ? ids[index + 1] : isComplete ? ids[0] : null,
    needsMoreForNext: isLastLoaded && !isComplete && context.hasNextPage,
  };
}

/**
 * Lista com mais uma página no fim, sem repetir ids.
 * @param {RecordListContext} context @param {string[]} newIds @param {{ hasNextPage: boolean, endCursor: string | null }} pageInfo
 * @returns {RecordListContext}
 */
export function appendRecordListPage(context, newIds, pageInfo) {
  const known = new Set(context.recordIds);
  return {
    ...context,
    recordIds: [...context.recordIds, ...newIds.filter((id) => !known.has(id))],
    hasNextPage: pageInfo.hasNextPage && Boolean(pageInfo.endCursor),
    endCursor: pageInfo.endCursor,
  };
}
